import { InputWithLabel } from '@adotel/components'
import {
  Button,
  FormControlLabel,
  Grow,
  Switch,
  Typography
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { matchSorter } from 'match-sorter'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import CustomCheckBox from '../../../../components/CustomCheckBox'
import { convertPersianNumbersToEnglishNumbers } from '../../../../helpers/numbers'
import { getTranslate } from '../../../../localization'
import { ItemsInCart } from '../../../../types/cart'
import { Currency } from '../../../../types/currency'
import { CheckBoxDataType } from '../../../../types/customCheckBox'
import {
  Entities,
  RoomForSearch,
  SearchAvailabilityInput
} from '../../../../types/searchAvailability'
import { SelectedWalletType } from '../../../../types/wallet'
import ChannelBalance from '../ChannelBalance'
import ContentFitList from '../ContentFitList'
import PaymentWallet from '../PaymentWallet'
import PriceSlider from '../PriceSlider'
import TotalPrice from '../TotalPrice'
import useStyles from './styles/index.styles'

type Props = {
  searchData: Entities | undefined
  searchAvailabilityInput: SearchAvailabilityInput
  walletTypes: {
    name: string
    id: string
    currency: Currency
    cashBalance: number
    remainingCreditBalance: number
  }[]
  selectedWalletType: SelectedWalletType
  setSelectedWalletType: (
    value: React.SetStateAction<SelectedWalletType>
  ) => void

  setSearchAvailabilityInput: React.Dispatch<
    React.SetStateAction<SearchAvailabilityInput>
  >
  setFilteredRoomType: React.Dispatch<React.SetStateAction<RoomForSearch[]>>
  setSelectedProviders: React.Dispatch<React.SetStateAction<string[]>>
  selectedProviders: string[]
  setselectedRoomTypes: React.Dispatch<React.SetStateAction<string[]>>
  selectedRoomTypes: string[]
  setSelectedRoomFacilities: React.Dispatch<React.SetStateAction<string[]>>
  selectedRoomFacilities: string[]
  priceRange: number[]
  setPriceRange: React.Dispatch<React.SetStateAction<number[]>>
  isUserPriceChange: React.MutableRefObject<boolean>
  itemsInCart: ItemsInCart
  initialData: {
    uniqueRoomFacilities: CheckBoxDataType[]
    uniqueProviders: CheckBoxDataType[]
    roomTypeNames: {
      label: string
      value: string
    }[]
  } | null
  setInitialData: React.Dispatch<
    React.SetStateAction<{
      uniqueRoomFacilities: CheckBoxDataType[]
      uniqueProviders: CheckBoxDataType[]
      roomTypeNames: {
        label: string
        value: string
      }[]
    } | null>
  >
  maxPriceRange: number
}

const SearchAvailibilityFilters = ({
  searchData,
  searchAvailabilityInput,
  setSearchAvailabilityInput,
  walletTypes,
  selectedWalletType,
  setSelectedWalletType,
  selectedProviders,
  setSelectedProviders,
  setselectedRoomTypes,
  selectedRoomTypes,
  selectedRoomFacilities,
  setSelectedRoomFacilities,
  priceRange,
  setPriceRange,
  isUserPriceChange,
  itemsInCart,
  setFilteredRoomType,
  initialData,
  setInitialData,
  maxPriceRange
}: Props) => {
  const classes = useStyles()
  const params = useParams<{ channelId: string; code: string; name: string }>()
  const min = 0
  const [searchInputValue, setsearchInputValue] = useState<string>('')
  const [allReservedRoomAmount, setAllReservedRoomAmount] = useState<{
    price: number
    count: number
  }>({ price: 0, count: 0 })

  const handlePriceChange = (newRange: number[]) => {
    isUserPriceChange.current = true
    setPriceRange(newRange)
  }
  useEffect(() => {
    isUserPriceChange.current = false // mark it as NOT user change
    setPriceRange([min, maxPriceRange])
  }, [min, maxPriceRange])

  const handleResetAll = () => {
    setSelectedRoomFacilities([])
    setselectedRoomTypes([])
    setSelectedProviders([])
    setPriceRange([min, maxPriceRange])
  }

  useEffect(() => {
    const { totalCount, totalPrice } = itemsInCart.items.reduce(
      (acc, item) => {
        acc.totalCount += item.quantity
        acc.totalPrice += item.quantity * item.price!
        return acc
      },
      { totalCount: 0, totalPrice: 0 }
    )

    setAllReservedRoomAmount({ count: totalCount, price: totalPrice })
  }, [itemsInCart.items])

  useEffect(() => {
    const fullRoomTypes = searchData?.rooms || []
    const searchfilteredRoomTypes = searchInputValue
      ? matchSorter(fullRoomTypes, searchInputValue, {
          keys: ['roomTypeName', 'roomTypeCode']
        })
      : fullRoomTypes

    if (selectedRoomTypes.length === 0 || selectedRoomTypes.includes('All')) {
      setFilteredRoomType(searchfilteredRoomTypes)
    } else {
      const filtered = searchfilteredRoomTypes.filter((room) =>
        selectedRoomTypes.includes(room.roomTypeCode)
      )
      setFilteredRoomType(filtered)
    }
  }, [searchInputValue, selectedRoomTypes, searchData])

  useEffect(() => {
    if (!initialData && searchData?.rooms) {
      const facilities: { code: string; label: string }[] = []
      const providers: { code: string; label: string }[] = []
      const roomTypeNames = [{ label: 'All', value: 'All' }]

      searchData.rooms.forEach((room) => {
        roomTypeNames.push({
          value: room.roomTypeCode,
          label: room.roomTypeName
        })
        room.facilities.forEach((f) =>
          facilities.push({ code: f.ID, label: f.Name })
        )
        room.ratePlans.forEach((rp) =>
          providers.push({
            code: rp.displayProviderCode,
            label: rp.providerName
          })
        )
      })

      const uniqueRoomFacilities = Array.from(
        new Map(facilities.map((f) => [f.code, f])).values()
      ).map((d) => ({
        title: d.label,
        value: d.code,
        checked: false
      }))

      const uniqueProviders = Array.from(
        new Map(providers.map((p) => [p.code, p])).values()
      ).map((d) => {
        return {
          title: d.label,
          value: d.code,
          checked: false
        }
      })

      setInitialData({ uniqueRoomFacilities, uniqueProviders, roomTypeNames })
    }
  }, [searchData, initialData])

  const uniqueRoomFacilities = initialData?.uniqueRoomFacilities || []
  const uniqueProviders = initialData?.uniqueProviders || []
  const roomTypeNames = initialData?.roomTypeNames || [
    { label: 'All', value: 'All' }
  ]

  return (
    <div>
      <ChannelBalance channelName={params.name} />
      <Grow in={!!allReservedRoomAmount.count} unmountOnExit>
        <div>
          <TotalPrice
            details={allReservedRoomAmount}
            currency={searchAvailabilityInput.currency}
          />
        </div>
      </Grow>
      <PaymentWallet
        title={getTranslate('Payment Method')}
        options={walletTypes}
        selectedWalletType={selectedWalletType}
        setSelectedWalletType={setSelectedWalletType}
      />
      <div className={classes.filters}>
        <div className={classes.filterHeader}>
          <Typography variant="body2">{getTranslate('Filters')}</Typography>
          <Button variant="text" onClick={handleResetAll}>
            <Typography className={classes.reset} variant="h5">
              {getTranslate('Reset All')}
            </Typography>
          </Button>
        </div>
        <div className={classes.horizentalSeprator}></div>
        <div className={classes.search}>
          <Typography
            variant="h5"
            style={{ marginBottom: 10 }}
            className={classes.filterText}>
            {getTranslate('Search By Name')}
          </Typography>
          <InputWithLabel
            placeholder={getTranslate('Enter Room Type Name')}
            inputSize="small"
            onChange={(event) => {
              setsearchInputValue(
                convertPersianNumbersToEnglishNumbers(event.currentTarget.value)
              )
            }}
            startIcon={<SearchIcon />}
          />
        </div>
        <div className={classes.switchContainer}>
          <FormControlLabel
            control={
              <Switch
                checked={!searchAvailabilityInput.showUnavailableRatePlans}
                onChange={() =>
                  setSearchAvailabilityInput({
                    ...searchAvailabilityInput,
                    showUnavailableRatePlans:
                      !searchAvailabilityInput.showUnavailableRatePlans
                  })
                }
                color="primary"
                size="small"
              />
            }
            label={getTranslate('Show Available Rate Plans')}
            classes={{ label: classes.switchLabel }}
          />
        </div>
        <PriceSlider
          label={getTranslate('Price Range For 1 Night')}
          currency={searchAvailabilityInput.currency}
          min={min}
          max={maxPriceRange}
          price={priceRange}
          setPrice={handlePriceChange}
        />
        {uniqueProviders.length > 0 ? (
          <CustomCheckBox
            header={getTranslate('Providers')}
            options={uniqueProviders}
            selectedItems={selectedProviders}
            setSelectedItems={setSelectedProviders}
            includesAll
          />
        ) : (
          <></>
        )}
        {roomTypeNames.length > 0 ? (
          <ContentFitList
            header={getTranslate('Room Type ')}
            options={roomTypeNames}
            multiSelectedMode
            selectedItems={selectedRoomTypes}
            setSelectedItems={setselectedRoomTypes}
          />
        ) : (
          <></>
        )}
        {uniqueRoomFacilities.length > 0 && (
          <CustomCheckBox
            header={getTranslate('Room Facilities')}
            options={uniqueRoomFacilities}
            selectedItems={selectedRoomFacilities}
            setSelectedItems={setSelectedRoomFacilities}
          />
        )}
      </div>
    </div>
  )
}

export default SearchAvailibilityFilters
